"use client"

import { Button } from "@/components/ui/button"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Check, Star, ArrowRight, Plus, BarChart, ShoppingBag, Mail } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Pricing } from "@/components/Pricing"
import { EmailSignupForm } from "@/components/EmailSignupForm"
import { captureEvent } from "@/utils/analytics";

const Index = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation(["translation", "landing", "pricing", "testimonials"])
  const currentLang = i18n.language

  // Determine next language and button label based on current language
  const nextLang = currentLang === "en" ? "vi" : "en"
  const label = nextLang === "en" ? "English" : "Tiếng Việt"

  // Safely get translation arrays with fallbacks
  const benefitsItems = Array.isArray(t("landing:benefits.items", { returnObjects: true }))
    ? t("landing:benefits.items", { returnObjects: true })
    : []

  const personasItems = Array.isArray(t("landing:personas.items", { returnObjects: true }))
    ? t("landing:personas.items", { returnObjects: true })
    : []

  const featuresItems = Array.isArray(t("landing:features.items", { returnObjects: true }))
    ? t("landing:features.items", { returnObjects: true })
    : []

  const testimonials = Array.isArray(t("testimonials:testimonials", { returnObjects: true }))
    ? t("testimonials:testimonials", { returnObjects: true })
    : []

  const finalCtaBenefits = Array.isArray(t("landing:finalCta.benefits", { returnObjects: true }))
    ? t("landing:finalCta.benefits", { returnObjects: true })
    : []

  const faqItems = Array.isArray(t("landing:faq.items", { returnObjects: true }))
    ? t("landing:faq.items", { returnObjects: true })
    : []

  const footerSections = Array.isArray(t("landing:footer.sections", { returnObjects: true }))
    ? t("landing:footer.sections", { returnObjects: true })
    : []

  const footerLegalLinks = Array.isArray(t("landing:footer.legalLinks", { returnObjects: true }))
    ? t("landing:footer.legalLinks", { returnObjects: true })
    : []

  // Language switcher that updates URL parameter
  const handleLanguageChange = () => {
    // Update the language in i18n
    i18n.changeLanguage(nextLang)

    // Update URL with language parameter
    const params = new URLSearchParams(location.search)
    params.set("lang", nextLang)

    navigate(
      {
        pathname: location.pathname,
        search: params.toString(),
      },
      { replace: true },
    )
  }

  const handleStartTrial = () => {
    // Store default plan selection
    sessionStorage.setItem("selectedPlan", "basic")
    sessionStorage.setItem("billingCycle", "yearly")

    // Track trial start in PostHog
    captureEvent("start_trial", { plan: "basic", billingCycle: "yearly", source: "Index" });

    // Navigate to brand setup
    navigate("/brand-setup/default")
  }

  const handleSeeDemo = () => {
    navigate("/demo")
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="text-2xl font-bold text-blue-600">Passes™</div>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <a href="#features" className="text-gray-600 hover:text-blue-600 px-3 py-2">
                {t("landing:navigation.features", "Features")}
              </a>
              <a href="#pricing" className="text-gray-600 hover:text-blue-600 px-3 py-2">
                {t("landing:navigation.pricing", "Pricing")}
              </a>
              <a href="#testimonials" className="text-gray-600 hover:text-blue-600 px-3 py-2">
                {t("landing:navigation.testimonials", "Testimonials")}
              </a>
              <a href="#faq" className="text-gray-600 hover:text-blue-600 px-3 py-2">
                {t("landing:navigation.faq", "FAQ")}
              </a>
              <Button onClick={handleLanguageChange} variant="outline" size="sm" className="ml-4">
                {label}
              </Button>
            </div>
            <div className="md:hidden flex items-center">
              <Button onClick={handleLanguageChange} variant="outline" size="sm">
                {label}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-10 md:mb-0">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
                {t("hero.title", "Stop Losing Customers You Already Paid For")}
              </h1>
              <p className="text-xl text-gray-600 mb-8 max-w-lg">
                {t(
                  "hero.description",
                  "Your customers are forgetting about you. Put your business in their phone with digital loyalty passes that bring them back automatically — without spending more on ads.",
                )}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700" onClick={handleStartTrial}>
                  {t("hero.startTrial", "Start Free 14-Day Trial")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button size="lg" variant="outline" onClick={handleSeeDemo}>
                  {t("hero.seeDemo", "See 2-Minute Demo")}
                </Button>
              </div>
            </div>
            <div className="md:w-1/2">
              <div className="relative">
                <div className="absolute -top-6 -left-6 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
                <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
                <div className="absolute -bottom-8 left-20 w-64 h-64 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
                <div className="relative">
                  <img
                    src="/images/hero-card.png"
                    alt="Salon Loyalty Pass Example"
                    className="w-full max-w-md mx-auto rounded-xl shadow-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Email Signup Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="md:w-1/2">
              <h2 className="text-3xl font-bold mb-4">Stay Updated with Passes</h2>
              <p className="text-blue-100 text-lg mb-6">
                Get the latest updates, tips, and special offers delivered straight to your inbox.
              </p>
              <div className="flex items-center gap-3">
                <Mail className="h-6 w-6" />
                <p className="text-sm">Powered by Resend</p>
              </div>
            </div>
            <div className="md:w-1/2 w-full">
              <Card className="p-6 bg-white text-gray-800">
                <EmailSignupForm />
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">{t("landing:benefits.title", "What You Get")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Map through benefits */}
            {benefitsItems.map((benefit, index) => (
              <div key={index} className="bg-blue-50 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  {index === 0 ? (
                    <Plus className="h-6 w-6 text-blue-600" />
                  ) : index === 1 ? (
                    <BarChart className="h-6 w-6 text-blue-600" />
                  ) : (
                    <Check className="h-6 w-6 text-blue-600" />
                  )}
                </div>
                <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Who Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">{t("landing:personas.title", "For Who?")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Map through personas */}
            {personasItems.map((persona, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow text-center"
              >
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  {index === 0 ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-10 w-10 text-orange-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
                      />
                    </svg>
                  ) : index === 1 ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-10 w-10 text-orange-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    </svg>
                  ) : (
                    <ShoppingBag className="h-10 w-10 text-orange-600" />
                  )}
                </div>
                <h3 className="text-xl font-semibold mb-2">{persona.title}</h3>
                <p className="text-gray-600">{persona.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            {t("landing:features.title", "Features You'll Love")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Map through features */}
            {featuresItems.map((feature, index) => (
              <div key={index} className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    {index === 0 ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    ) : index === 1 ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    ) : index === 2 ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    ) : index === 3 ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    ) : index === 4 ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                      />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    )}
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">{t("testimonials:title", "What Our Customers Say")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Map through testimonials */}
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <Card key={index} className="p-6">
                <div className="flex items-center mb-4">
                  <Avatar className="h-12 w-12 mr-4">
                    <AvatarImage src={`https://i.pravatar.cc/150?img=${index + 1}`} />
                    <AvatarFallback>{testimonial.name ? testimonial.name[0] : "U"}</AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-semibold">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600">{testimonial.content}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Integrations */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            {t("landing:integrations.title", "Tools & Integrations")}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center justify-items-center">
            <div className="grayscale hover:grayscale-0 transition-all">
              <img src="/placeholder.svg?height=60&width=120" alt="Apple Wallet" className="h-12" />
            </div>
            <div className="grayscale hover:grayscale-0 transition-all">
              <img src="/placeholder.svg?height=60&width=120" alt="Google Pay" className="h-12" />
            </div>
            <div className="grayscale hover:grayscale-0 transition-all">
              <img src="/placeholder.svg?height=60&width=120" alt="Shopify" className="h-12" />
            </div>
            <div className="grayscale hover:grayscale-0 transition-all">
              <img src="/placeholder.svg?height=60&width=120" alt="Zalo" className="h-12" />
            </div>
            <div className="grayscale hover:grayscale-0 transition-all">
              <img src="/placeholder.svg?height=60&width=120" alt="Facebook" className="h-12" />
            </div>
            <div className="grayscale hover:grayscale-0 transition-all">
              <img src="/placeholder.svg?height=60&width=120" alt="Instagram" className="h-12" />
            </div>
            <div className="grayscale hover:grayscale-0 transition-all">
              <img src="/placeholder.svg?height=60&width=120" alt="Resend" className="h-12" />
            </div>
            <div className="grayscale hover:grayscale-0 transition-all">
              <img src="/placeholder.svg?height=60&width=120" alt="Stripe" className="h-12" />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-4">{t("pricing:title", "Simple Pricing That Works")}</h2>
          <p className="text-xl text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            {t("pricing:subtitle", "Choose the right plan for your business needs and budget")}
          </p>

          {/* Use the Pricing component */}
          <div className="py-8">
            <Pricing />
          </div>
        </div>
      </section>

      {/* What You Get (Final CTA) */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            {t("landing:finalCta.title", "What You Get When You Sign Up Today")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <ul className="space-y-6">
                {finalCtaBenefits.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <div className="bg-blue-100 rounded-full p-1 mr-4 mt-1">
                      <Check className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{benefit.title}</h3>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700" onClick={handleStartTrial}>
                  {t("landing:finalCta.buttonText", "Start Your Free Trial")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
                <div className="flex items-center mb-6">
                  <Avatar className="h-12 w-12 mr-4">
                    <AvatarImage src="https://i.pravatar.cc/150?img=4" />
                    <AvatarFallback>MN</AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-semibold">{t("landing:finalCta.testimonial.name", "Mai Nguyen")}</div>
                    <div className="text-sm text-gray-500">
                      {t("landing:finalCta.testimonial.role", "Nail Salon Owner")}
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 mb-4">
                  {t(
                    "landing:finalCta.testimonial.content",
                    "I was spending $500/month on Facebook ads just to get my existing customers to come back. Now I spend $30/month on Passes and get better results. My ROI is over 1,500%.",
                  )}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t("landing:finalCta.testimonial.verified", "Verified Customer")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            {t("landing:faq.title", "Frequently Asked Questions")}
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index + 1}`}>
                <AccordionTrigger>{faq.question}</AccordionTrigger>
                <AccordionContent>{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="text-2xl font-bold mb-4">Passes™</div>
              <p className="text-gray-400 mb-4">
                {t("landing:footer.description", "Digital loyalty passes that bring customers back automatically.")}
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                      clipRule="evenodd"
                    />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-white">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </a>
              </div>
            </div>
            {footerSections.map((section, index) => (
              <div key={index}>
                <h3 className="text-lg font-semibold mb-4">{section.title}</h3>
                <ul className="space-y-2">
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <a href="#" className="text-gray-400 hover:text-white">
                        {link}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 mb-4 md:mb-0">
              {t("landing:footer.copyright", "© 2025 Passes™. All rights reserved.")}
            </div>
            <div className="flex space-x-6">
              {footerLegalLinks.map((link, index) => (
                <a key={index} href="#" className="text-gray-400 hover:text-white">
                  {link}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Index
